import { GenericResponse } from './controllers/service-models.js'
import { Result } from '@mobily/ts-belt'

export type OKKOResponse = 'OK' | 'KO';

type Jsonable = string | number | boolean | null | undefined | readonly Jsonable[] | { readonly [key: string]: Jsonable } | { toJSON(): Jsonable };

export interface Context {
  needLogin?: boolean;
  needRefresh?: boolean;
  needRegistration?: boolean;
  request?: { url: string, body?: string },
  response?: { statusText: string, body?: string },
  error?: Jsonable,
  data?: Jsonable,
}

class BaseError extends Error {
  public readonly context?: Context;

  constructor(message: string, options: { cause?: unknown, context?: Context } = {}) {
    const { cause, context } = options;
    super(message, { cause })
    Object.defineProperty(this, 'name', {
      value: new.target.name,
      enumerable: false,
      configurable: true,
    });
    this.context = context;
  }
}

export class RaissoError extends BaseError {

  public readonly title?: string;
  public readonly code?: string;

  constructor(message: string | GenericResponse, options?: { cause?: unknown, context?: Context }) {
    super(typeof message === 'string' ? message : (message.message ?? "Errore"), options);
    Object.defineProperty(this, 'name', {
      value: new.target.name,
      enumerable: false,
      configurable: true
    });

    Object.setPrototypeOf(this, new.target.prototype);
    Error.captureStackTrace(this, this.constructor);

    if (typeof message !== 'string') {
      this.title = message.title;
      this.code = message.detail;
    }
  }
}

export type ServiceResponse<T> = Promise<Result<T, RaissoError>>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Ctor<T> = new (...args: any[]) => T;

export function errorFactory<T extends BaseError, C extends Ctor<T>>(
  ctor: C,
  ...params: ConstructorParameters<C>
): InstanceType<C> {
  return new ctor(...params) as InstanceType<C>;
}

export interface JwtData {
  idSSO: number;
  ente: boolean;
  iss: string;
  exp: number;
}

export class RequestNotAllowedError extends Error {
  public readonly code = 'EREQUEST_NOT_ALLOWED' as const;

  private constructor(public readonly error: RaissoError) {
    super(error.message, { cause: error.cause });

    Object.defineProperty(this, "name", {
      value: new.target.name,
      enumerable: false,
      configurable: true,
    });
    Object.setPrototypeOf(this, new.target.prototype);
    Error.captureStackTrace(this, this.constructor);
  }

  static fromRaisso(error: RaissoError) {
    return new RequestNotAllowedError(error);
  }
}

export class InvalidOperationError extends Error {
  public readonly response: { statusText: string, body?: string } | undefined;
  public readonly code = 'EINVALID_OPERATION' as const;
  public readonly forceLogout: boolean;

  private constructor(public readonly error: RaissoError) {
    super(error.message, { cause: error.cause });
    Object.defineProperty(this, "name", {
      value: new.target.name,
      configurable: true,
      enumerable: false
    });
    Object.setPrototypeOf(this, new.target.prototype);
    Error.captureStackTrace(this, this.constructor);

    this.response = error.context?.response;
    this.forceLogout = error.context?.needLogin ?? false
  }

  static fromRaisso(error: RaissoError) {
    return new InvalidOperationError(error);
  }
}

export class NeedLoginError extends Error {
  public readonly code = 'ENEED_LOGIN' as const;
  public readonly forceLogout = true;

  private constructor(public readonly error: RaissoError) {
    super(error.message, { cause: error.cause });

    Object.defineProperty(this, "name", {
      value: new.target.name,
      enumerable: false,
      configurable: true
    });
    Object.setPrototypeOf(this, new.target.prototype);
    Error.captureStackTrace(this, this.constructor);
  }

  static fromRaisso(error: RaissoError) {
    return new NeedLoginError(error);
  }
}

export class InvalidResponseError extends Error {
  public readonly code = 'EINVALID_RESPONSE' as const;
  public readonly forceLogout: boolean;
  public data: unknown;

  private constructor(public readonly error: RaissoError) {
    super(error.message, { cause: error.cause });

    Object.defineProperty(this, "name", {
      value: new.target.name,
      enumerable: false,
      configurable: true
    });
    Object.setPrototypeOf(this, new.target.prototype);
    Error.captureStackTrace(this, this.constructor);

    this.forceLogout = error.context?.needLogin ?? false
    this.data = error.context?.data;
  }

  static fromRaisso(error: RaissoError) {
    return new InvalidResponseError(error);
  }

}

export class NeedLogoutError extends Error {
  public readonly code = 'ENEED_LOGOUT' as const;
  public readonly forceLogout = true;

  private constructor(public readonly error: RaissoError) {
    super(error.message, { cause: error.cause });

    Object.defineProperty(this, "name", {
      value: new.target.name,
      enumerable: false,
      configurable: true
    });
    Object.setPrototypeOf(this, new.target.prototype);
    Error.captureStackTrace(this, this.constructor);
  }

  static fromRaisso(error: RaissoError) {
    return new NeedLogoutError(error);
  }
}

export class NeedRegistrationError extends Error {
  public readonly code = 'ENEED_REGISTRATION' as const;
  public readonly forceLogout = true

  private constructor(error: RaissoError) {
    super(error.message, { cause: error.cause });

    Object.defineProperty(this, "name", {
      value: new.target.name,
      enumerable: false,
      configurable: true
    })
    Object.setPrototypeOf(this, new.target.prototype);
    Error.captureStackTrace(this, this.constructor);
  }

  static fromRaisso(error: RaissoError) {
    return new NeedRegistrationError(error);
  }
}
